import React, { useCallback, useMemo, useState } from 'react'
import { ENDPOINTS } from 'apps/placeme/src/constants/endpoints'
import { useSelector } from 'react-redux'
import { RootState } from 'apps/placeme/src/redux/store'
import { TileSectionIds } from '@dataplace.ai/constants'
import styled from 'styled-components'
import isEmpty from 'lodash/isEmpty'
import { AttractivenessTile } from './AttractivenessTile'
import { useAttractivenessFormData } from './AttractivenessForm/hooks/useAttractivenessFormData'
import {
  AttractivenessFormFields,
  AttractivenessFormState,
  FormFieldValue,
  IAttractivenessTileBody,
  IAttractivenessTileData,
} from './@types/IAttractivenessTileData'

const ContentWrapper = styled.div`
  padding: 1.25rem;
`

const initialState: AttractivenessFormState = {
  includeOwnChain: false,
  businessType: null,
  ageBuckets: [],
  trafficType: null,
  transportMeans: null,
  dayparts: [],
  weekparts: [],
  market: null,
}

const requiredFields = [
  AttractivenessFormFields.businessType,
  AttractivenessFormFields.trafficType,
  AttractivenessFormFields.transportMeans,
  AttractivenessFormFields.ageBuckets,
  AttractivenessFormFields.dayparts,
  AttractivenessFormFields.weekparts,
  AttractivenessFormFields.market,
]

export const AttractivenessModel: React.FC<{
  data: IAttractivenessTileData,
  tileId: string,
}> = ({
  data,
  tileId,
}) => {
  const {
    analyseId,
    value: {
      lat, lng,
    } = {},
  } = useSelector((state: RootState) => state.location)
  const [formState, setFormState] = useState<AttractivenessFormState>(initialState)

  const areAllFieldsFilled = useMemo(() => requiredFields.every(key => !isEmpty(formState[key])), [formState])

  const formDataObject = useAttractivenessFormData(formState)

  const setFormValue = useCallback(
    (key: string, value: FormFieldValue) => {
      setFormState(prev => ({
        ...prev,
        [key]: value,
      }))
    },
    [],
  )

  const body: IAttractivenessTileBody = useMemo(() => ({
    coordinates: {
      lat: lat!,
      lng: lng!,
    },
    reportId: analyseId!,
    includeOwnChain: formState.includeOwnChain,
    businessType: formState.businessType,
    ageBuckets: formState.ageBuckets,
    traffic: {
      type: formState.trafficType,
      dayparts: formState.dayparts,
      weekparts: formState.weekparts,
    },
    transportMeans: formState.transportMeans,
    market: formState.market,
  }), [formState])

  return (
    <ContentWrapper>
      <AttractivenessTile
        areAllFieldsFilled={areAllFieldsFilled}
        body={body}
        categoryId={TileSectionIds.POTENTIAL}
        data={data}
        endpoint={ENDPOINTS.ATTRACTIVENESS_TILE}
        formDataObject={formDataObject}
        setFormValue={setFormValue}
        tileId={tileId}
      />
    </ContentWrapper>
  )
}
